import {
  awaitImgs,
  configureVideoElementForAutoplay,
  div,
  el, hide,
  hideAllChildren, rewindAndPause, rewindAndPlay,
  show
} from '@megaphonenpm/mputils/src/DOM.js';
import {MegaScreen} from "./MegaScreen.js";
import {isNil, notEmpty, notNil} from '@megaphonenpm/mputils/src/utils.js';
import {CHANGE, COMPLETE, FAIL} from '@megaphonenpm/mputils/src/constants.js';
import {EventDispatcher} from '@megaphonenpm/mputils/src/EventDispatcher.js';
import {MediaContainerEvent} from './MediaContainerEvent.js';
import {PlayVideoTask} from './PlayVideoTask.js';


export class MediaContainer extends EventDispatcher {
  constructor (name) {
    super();
    this.name = name;
    this.uri = null;
    this.lastRequestedURI = null;
    this.muted = null;
    this.loop = null;
    this.boundMediaCacheChangeListener = this.mediaCacheChangeListener.bind(this);

    this.init();
  }

  init () {
    // Create container element to hold image and video
    this.view = div(null, 'mediaContainer');

    // Create image element
    this.img = el('img', `${this.name}Image`, 'mediaAsset');

    // Create video element
    this.video = el('video', `${this.name}Video`, 'mediaAsset');
    configureVideoElementForAutoplay(this.video);
    this.playVideoTask = null;
    this.setMuted(false);
    this.setLoop(true);

    this.view.append(this.img);
    this.view.append(this.video);

    this.hideAll();
  }

  /**
   * Handles CHANGE events from the global MediaCache. If an asset loads AFTER this MediaContainer's
   * URI has been assigned, then this MediaContainer checks that asset to determine if it matches
   * this MediaContainer's most-recently assigned URI. If the asset matches this MediaContainer's
   * most-recently assigned URI, then the asset was not available when that URI was assigned, so
   * now that the asset IS available, call setURI() to display it.
   *
   * @param e
   */
  mediaCacheChangeListener (e) {
    let uriUpdatedInCache = e.detail.uri;
    if (uriUpdatedInCache === this.lastRequestedURI) {
      this.setURI(uriUpdatedInCache);
    }
  }

  /**
   * Assign the URI for an image or video to display on screen, causing the asset to load.
   *
   * @param value A URI referring to an image or video.
   * @returns {Promise<unknown>} A promise that resolves when the image or video is ready to be
   *          displayed.
   */
  setURI (value) {
    // Stop listening for CHANGE events from the global MediaCache. Unregistering the listener here
    // prevents multiple calls to setURI() on this MediaContainer from potentially creating a
    // backlog of MediaCache observation that leads to unwanted media being displayed, and potential
    // memory leaks. Treat each call to setURI() as a clean slate. MediaContainers can display one
    // asset at a time only, so MediaCache CHANGE events are irrelevant to this MediaContainer
    // except in the case of the most-recently requested URI.
    MegaScreen.globals.mediaCache.off(CHANGE, this.boundMediaCacheChangeListener);
    this.lastRequestedURI = value;

    // If, from the last call to setURI(), a video-play attempt is still in progress, abort it
    if (notNil(this.playVideoTask) && !this.playVideoTask.complete) {
      this.playVideoTask.quit();
    }

    return new Promise((resolve) => {
      let asset = MegaScreen.globals.mediaCache.get(value);

      if (isNil(asset)) {
        // If this branch has been entered, then the specified URI's asset was not found in the
        // global MediaCache.

        this.uri = null;
        hide(this.view);
        this.hideAll();

        if (notEmpty(value)) {
          // The requested URI is not empty, but the asset is null (didn't exist in the global
          // MediaCache), so the asset is either still loading (the likely case) or failed to load.
          // Assume the asset is still loading, and register to be notified when it finishes loading
          // so it can be displayed when it's ready.
          MegaScreen.globals.mediaCache.on(CHANGE, this.boundMediaCacheChangeListener);
        }

        // Either the the specified URI was empty, or the asset for the specified URI hasn't
        // finished loading, so this attempt to set this MediaContainer's URI has resolved. If the
        // asset finishes loading, then this.boundMediaCacheChangeListener() will be invoked,
        // causing this MediaContainer to automatically display the asset.
        resolve();
      } else {
        this.uri = value;
        if (asset.type.indexOf('image') !== -1) {
          // The new media asset is an image
          if (this.img.src !== asset.objectURL) {
            this.img.src = asset.objectURL;
          }
          awaitImgs(this.view).then(() => {
            show(this.img);
            rewindAndPause(this.video);
            hide(this.video);
            resolve();
          })
        } else if (asset.type.toLowerCase().includes('video')
          || asset.type.toLowerCase().includes('matroska')
          || asset.type.toLowerCase().includes('application/octet-stream')
          || this.uri.endsWith('mp4')
          || this.uri.endsWith('webm')
          || this.uri.endsWith('mov')) {

          // The new media asset is a video
          if (this.video.src !== asset.objectURL) {
            this.video.src = asset.objectURL;
          }

          // Note if the video fails to play, PlayVideoTask will automatically try to play it again
          this.playVideoTask = new PlayVideoTask(this.video);
          this.playVideoTask.initiator = this.name;

          this.playVideoTask.on(COMPLETE, () => {
            if (!this.muted && this.playVideoTask.muted) {
              // The current MediaContainer is NOT muted, but the PlayVideoTask is muted, so the
              // PlayVideoTask must have muted the video in order for the browser to allow it to
              // play.
              console.warn(`[MEDIA_CONTAINER (${this.name})] Video muted to allow playback. Dispatching UNMUTED_VIDEO_PLAY_REJECTED...`);
              this.dispatchEvent(MediaContainerEvent.UNMUTED_VIDEO_PLAY_REJECTED);
            }
            // The video is ready to be displayed, so resolve the setURI() Promise
            resolve();
          });

          this.playVideoTask.on(FAIL, () => {
            // The video permanently failed to play and no further attempt will be made to make it
            // play, so resolve the setURI() Promise.
            this.dispatchEvent(MediaContainerEvent.VIDEO_PLAY_REJECTED);
            resolve();
          });

          this.playVideoTask.start();

          // Show the video now, but don't resolve setURI()'s Promise until the video has actually
          // started playing. For direct uses of MediaContainer, showing the video before setURI()'s
          // Promise resolves can result in "empty content" before the video begins playing. To
          // guarantee no "empty content" appears before videos play, use DoubleBufferedMediaContainer.
          show(this.video);
          hide(this.img);
        }
      }
    });
  }

  /**
   * Enable or disable audio for the video asset.
   */
  setMuted (muted) {
    this.video.setAttribute('muted', muted.toString());
    this.video.muted = muted;
    this.muted = muted;
  }

  setLoop (loop) {
    if (loop === true) {
      this.loop = true;
      this.video.setAttribute('loop', '');
    } else {
      this.loop = false;
      this.video.removeAttribute('loop');
    }
  }

  hideAll () {
    rewindAndPause(this.video);
    hideAllChildren(this.view);
  }

  pause () {
    this.video.pause();
  }
}
